import { useAuth } from "@/context/AuthContext";
import { SaveDialogContent, SaveButton, SaveForm } from "@/components/SaveForm";
import { useEffect, useMemo, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { BadgeDollarSign, Pencil } from "lucide-react";
import { toast } from "sonner";

const inp = "bg-slate-950 border-slate-700 text-slate-100 mt-1";
const brl = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export default function PlatformSubscriptions() {
  const { user } = useAuth();
  const owner = user?.is_platform_owner === true;
  const [tenants, setTenants] = useState([]);
  const [plans, setPlans] = useState([]);
  const [modules, setModules] = useState([]);
  const [subs, setSubs] = useState([]);
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [t, p, m, s] = await Promise.all([
        api.get("/platform/tenants"),
        api.get("/platform/plans"),
        api.get("/platform/modules"),
        api.get("/platform/subscriptions"),
      ]);
      setTenants(t.data); setPlans(p.data); setModules(m.data.filter((x) => x.ativo !== false)); setSubs(s.data);
    } catch (e) { toast.error(apiErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const subMap = useMemo(() => Object.fromEntries(subs.map((s) => [s.tenant_id, s])), [subs]);

  const openEdit = (tenant) => {
    const current = subMap[tenant.id];
    setEdit(current ? {
      ...current,
      empresa: tenant.nome_fantasia,
      inherit_plan_modules: current.plan_id ? current.inherit_plan_modules !== false : false,
      module_ids: [...(current.module_ids || [])],
    } : {
      tenant_id: tenant.id,
      empresa: tenant.nome_fantasia,
      plan_id: "",
      status: "ATIVA",
      valor_mensal: 0,
      dia_vencimento: 10,
      module_ids: [],
      inherit_plan_modules: false,
      inicio: new Date().toISOString().slice(0, 10),
      trial_ate: "",
      observacoes: "",
    });
  };

  const changePlan = (planId) => {
    const plan = plans.find((p) => p.id === planId);
    setEdit({
      ...edit,
      plan_id: planId,
      valor_mensal: plan?.valor_mensal ?? edit.valor_mensal,
      module_ids: [...(plan?.module_ids || [])],
      inherit_plan_modules: true,
    });
  };

  const toggleModule = (id) => {
    const cur = edit.module_ids || [];
    setEdit({ ...edit, module_ids: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] });
  };

  const setInheritance = (enabled) => {
    const plan = plans.find((p) => p.id === edit.plan_id);
    setEdit({
      ...edit,
      inherit_plan_modules: enabled,
      module_ids: enabled ? [...(plan?.module_ids || [])] : [...(edit.module_ids || [])],
    });
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        plan_id: edit.plan_id || null,
        status: edit.status,
        valor_mensal: edit.valor_mensal,
        dia_vencimento: edit.dia_vencimento,
        module_ids: edit.module_ids || [],
        inherit_plan_modules: !!edit.plan_id && edit.inherit_plan_modules !== false,
        inicio: edit.inicio,
        trial_ate: edit.trial_ate || null,
        observacoes: edit.observacoes || "",
      };
      await api.put(`/platform/subscriptions/${edit.tenant_id}`, owner ? payload : { plan_id: payload.plan_id, module_ids: payload.module_ids, inherit_plan_modules: payload.inherit_plan_modules });
      toast.success("Assinatura atualizada.");
      setEdit(null); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold text-slate-50">Assinaturas</h1>
        <p className="text-sm text-slate-400">{owner ? "Plano, mensalidade, vencimento e módulos liberados por empresa." : "Gerencie os módulos das assinaturas existentes. Valores disponíveis para consulta; alterações comerciais são exclusivas do proprietário."}</p>
      </div>

      <Card className="card-metal p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[820px]">
            <thead><tr className="bg-slate-950 text-slate-400 text-xs uppercase font-mono">
              <th className="text-left px-4 py-3">Empresa</th>
              <th className="text-left px-4 py-3">Plano</th>
              <th className="text-center px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Mensalidade</th>
              <th className="text-center px-4 py-3">Vencimento</th>
              <th className="text-center px-4 py-3">Módulos</th>
              <th className="text-right px-4 py-3">Ações</th>
            </tr></thead>
            <tbody>
              {tenants.map((t) => {
                const s = subMap[t.id];
                return (
                  <tr key={t.id} className="border-t border-slate-800">
                    <td className="px-4 py-3 font-medium text-slate-100">{t.nome_fantasia}</td>
                    <td className="px-4 py-3 text-slate-300">{s?.plano || "Sem plano"}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={s?.status === "ATIVA" ? "text-emerald-400" : s?.status === "TRIAL" ? "text-sky-400" : s ? "text-amber-400" : "text-slate-500"}>
                        {s?.status || "Não configurada"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-slate-300">{s ? brl(s.valor_mensal) : "—"}</td>
                    <td className="px-4 py-3 text-center text-slate-400">{s ? `Dia ${s.dia_vencimento}` : "—"}</td>
                    <td className="px-4 py-3 text-center text-slate-400">{s?.module_ids?.length || 0}</td>
                    <td className="px-4 py-3 text-right"><Button disabled={!owner && !s} size="sm" variant="ghost" onClick={() => openEdit(t)}><Pencil className="h-3.5 w-3.5 mr-1" /> Configurar</Button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
        <SaveDialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-w-2xl">
          <SaveForm disabled={busy} onSave={save} className="space-y-4">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><BadgeDollarSign className="h-5 w-5 text-violet-300" /> Assinatura · {edit?.empresa}</DialogTitle></DialogHeader>
            {edit && <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label>Plano</Label>
              <Select disabled={!owner} value={edit.plan_id || "SEM_PLANO"} onValueChange={(v) => v === "SEM_PLANO" ? setEdit({ ...edit, plan_id: "", inherit_plan_modules: false }) : changePlan(v)}>
                <SelectTrigger className={inp}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                  <SelectItem value="SEM_PLANO">Sem plano</SelectItem>
                  {plans.filter((p) => p.ativo !== false).map((p) => <SelectItem key={p.id} value={p.id}>{p.nome}{" · " + brl(p.valor_mensal)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select disabled={!owner} value={edit.status} onValueChange={(v) => setEdit({ ...edit, status: v })}>
                <SelectTrigger className={inp}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                  {["TRIAL","ATIVA","ATRASADA","SUSPENSA","CANCELADA"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
<div><Label>Valor mensal</Label><Input readOnly={!owner} type="number" step="0.01" className={inp} value={edit.valor_mensal ?? 0} onChange={(e) => setEdit({ ...edit, valor_mensal: e.target.value })} /></div>
<div><Label>Dia de vencimento (1 a 28)</Label><Input readOnly={!owner} type="number" min="1" max="28" className={inp} value={edit.dia_vencimento ?? 10} onChange={(e) => setEdit({ ...edit, dia_vencimento: e.target.value })} /></div>
{owner && <div><Label>Início</Label><Input type="date" className={inp} value={edit.inicio || ""} onChange={(e) => setEdit({ ...edit, inicio: e.target.value })} /></div>}
{owner && <div><Label>Trial até</Label><Input type="date" className={inp} value={edit.trial_ate || ""} onChange={(e) => setEdit({ ...edit, trial_ate: e.target.value })} /></div>}

            <div className="sm:col-span-2">
              <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-800 bg-slate-950/50 px-3 py-3">
                <div>
                  <Label>Usar módulos do plano</Label>
                  <div className="mt-0.5 text-[11px] text-slate-500">
                    Ligado: alterações feitas no plano são aplicadas automaticamente nesta empresa.
                  </div>
                </div>
                <Switch
                  disabled={!edit.plan_id}
                  checked={!!edit.plan_id && edit.inherit_plan_modules !== false}
                  onCheckedChange={setInheritance}
                />
              </div>
            </div>

            <div className="sm:col-span-2">
              <div className="flex items-center justify-between gap-2">
                <Label>Módulos liberados</Label>
                {!!edit.plan_id && edit.inherit_plan_modules !== false && (
                  <span className="text-[11px] text-violet-300">Herdados do plano</span>
                )}
              </div>
              <div className={`mt-1 grid gap-2 sm:grid-cols-2 max-h-56 overflow-y-auto rounded-lg border border-slate-800 bg-slate-950/40 p-3 ${
                edit.plan_id && edit.inherit_plan_modules !== false ? "opacity-70" : ""
              }`}>
                {modules.map((m) => (
                  <label key={m.id} className="flex items-center justify-between gap-3 rounded-lg px-2 py-2 hover:bg-slate-900/60">
                    <span className="text-sm text-slate-200">{m.nome}</span>
                    <Switch
                      disabled={!!edit.plan_id && edit.inherit_plan_modules !== false}
                      checked={(edit.module_ids || []).includes(m.id)}
                      onCheckedChange={() => toggleModule(m.id)}
                    />
                  </label>
                ))}
              </div>
              {!!edit.plan_id && edit.inherit_plan_modules !== false && (
                <div className="mt-2 text-xs text-slate-500">
                  Para criar uma exceção somente para esta empresa, desligue “Usar módulos do plano”.
                </div>
              )}
            </div>

{owner && <div className="sm:col-span-2"><Label>Observações</Label><Input className={inp} value={edit.observacoes || ""} onChange={(e) => setEdit({ ...edit, observacoes: e.target.value })} /></div>}
            </div>}
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setEdit(null)}>Cancelar</Button>
              <SaveButton type="submit" disabled={busy} className="bg-violet-700 hover:bg-violet-600">Salvar assinatura</SaveButton>
            </DialogFooter>
          </SaveForm>
        </SaveDialogContent>
      </Dialog>
    </div>
  );
}
